import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaLink,
  FaSearch,
  FaSync,
  FaTimes,
  FaUnlink,
  FaBan,
  FaCheckCircle,
  FaCircle,
  FaUserPlus,
  FaChevronDown,
} from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';
import DiscordUserChip from './DiscordUserChip';
import './LinkedProfilesPanel.css';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatMoney = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
};

const mcHead = (nick) =>
  nick
    ? `https://mc-heads.net/avatar/${encodeURIComponent(nick)}/40`
    : null;

const emptyLink = () => ({
  discordId: '',
  nickname: '',
  uuid: '',
});

/**
 * Gestão de contas Discord ↔ Minecraft (linked_profiles).
 */
const LinkedProfilesPanel = ({
  stats,
  profiles = [],
  pendingCodes = [],
  total = 0,
  presenceFilter = 'all',
  query = '',
  loading = false,
  actionBusy = null,
  message = '',
  error = '',
  canEdit = true,
  canDelete = true,
  onPresenceFilter,
  onQueryChange,
  onSearch,
  onRefresh,
  onSelect,
  selected,
  onCloseDetail,
  onUnlink,
  onUpdate,
  onRevokeCode,
  onLink,
  linkOpen = false,
  onToggleLink,
  guildMembers = [],
  guildMembersLoading = false,
  guildMembersError = '',
  onReloadMembers,
}) => {
  const [editNick, setEditNick] = useState('');
  const [editMoney, setEditMoney] = useState('');
  const [editClan, setEditClan] = useState('');
  const [editOnline, setEditOnline] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [linkForm, setLinkForm] = useState(emptyLink);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberMenuOpen, setMemberMenuOpen] = useState(false);
  const memberPickerRef = useRef(null);
  const memberInputRef = useRef(null);

  useEffect(() => {
    if (!selected) return;
    setEditNick(selected.nickname || '');
    setEditMoney(
      typeof selected.money === 'number' ? String(selected.money) : '0'
    );
    setEditClan(selected.clan || '');
    setEditOnline(Boolean(selected.online));
  }, [selected]);

  useEffect(() => {
    if (!linkOpen) {
      setLinkForm(emptyLink());
      setSelectedMember(null);
      setMemberQuery('');
      setMemberMenuOpen(false);
    }
  }, [linkOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        memberPickerRef.current &&
        !memberPickerRef.current.contains(e.target)
      ) {
        setMemberMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    return guildMembers
      .filter((m) => {
        if (!q) return true;
        const hay = [m.displayName, m.globalName, m.username, m.nick, m.id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 80);
  }, [guildMembers, memberQuery]);

  const rawIdDraft = memberQuery.trim();
  const canUseRawId = /^\d{5,32}$/.test(rawIdDraft);

  const pickMember = (memberOrId) => {
    if (typeof memberOrId === 'string') {
      const id = memberOrId.trim();
      setSelectedMember({ id, displayName: id });
      setLinkForm((prev) => ({ ...prev, discordId: id }));
    } else if (memberOrId?.id) {
      setSelectedMember(memberOrId);
      setLinkForm((prev) => ({ ...prev, discordId: memberOrId.id }));
    }
    setMemberQuery('');
    setMemberMenuOpen(false);
  };

  const clearMember = () => {
    setSelectedMember(null);
    setLinkForm((prev) => ({ ...prev, discordId: '' }));
    setMemberQuery('');
  };

  const saveEdits = () => {
    if (!selected?.id || !onUpdate) return;
    const money = Number(editMoney);
    onUpdate(selected.id, {
      nickname: editNick.trim(),
      money: Number.isFinite(money) ? money : 0,
      clan: editClan.trim() || null,
      online: editOnline,
    });
  };

  const submitLink = (e) => {
    e.preventDefault();
    if (!onLink) return;
    const discordId = String(linkForm.discordId || '').trim();
    const nickname = String(linkForm.nickname || '').trim();
    if (!discordId || !nickname) return;
    onLink({
      discordId,
      nickname,
      uuid: String(linkForm.uuid || '').trim() || undefined,
    });
  };

  return (
    <section className="admin-section profiles-panel">
      <header className="profiles-panel__head">
        <div>
          <h1>
            <FaLink aria-hidden="true" /> Contas vinculadas
          </h1>
          <p>
            Discord ↔ Minecraft · money, clã e presença
            {total != null
              ? ` · ${total} resultado${total === 1 ? '' : 's'}`
              : ''}
          </p>
        </div>
        <div className="profiles-panel__head-actions">
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <FaSync className={loading ? 'admin-spin' : ''} aria-hidden="true" />
            Atualizar
          </button>
          {canEdit && (
            <button
              type="button"
              className={`admin-btn admin-btn--sm${linkOpen ? ' admin-btn--ghost' : ' admin-btn--primary'}`}
              onClick={onToggleLink}
            >
              {linkOpen ? (
                <FaTimes aria-hidden="true" />
              ) : (
                <FaUserPlus aria-hidden="true" />
              )}
              {linkOpen ? 'Fechar' : 'Vincular manualmente'}
            </button>
          )}
        </div>
      </header>

      {message && <p className="admin-success">{message}</p>}
      {error && (
        <div className="admin-alert" role="alert">
          {error}
        </div>
      )}

      {canEdit && linkOpen && (
        <form className="profiles-link" onSubmit={submitLink}>
          <header>
            <h2>Vincular conta manualmente</h2>
            <p>
              Cria o vínculo Discord ↔ Minecraft sem código. Se o UUID ficar
              em branco, usa o UUID offline do nick (servidor cracked).
            </p>
          </header>

          <div className="profiles-link__member">
            <span className="profiles-link__label">Membro Discord</span>
            {selectedMember?.id ? (
              <div className="profiles-link__selected">
                <DiscordUserChip
                  user={selectedMember}
                  userId={selectedMember.id}
                />
                <button
                  type="button"
                  className="admin-btn admin-btn--ghost admin-btn--sm"
                  onClick={clearMember}
                  aria-label="Trocar membro"
                >
                  <FaTimes aria-hidden="true" /> Trocar
                </button>
              </div>
            ) : (
              <div
                className={`profiles-member-picker${memberMenuOpen ? ' is-open' : ''}`}
                ref={memberPickerRef}
              >
                <div
                  className={`profiles-member-picker__control${guildMembersLoading ? ' is-loading' : ''}`}
                  onClick={() => {
                    if (guildMembersLoading) return;
                    setMemberMenuOpen(true);
                    if (!guildMembers.length) onReloadMembers?.();
                    setTimeout(() => memberInputRef.current?.focus(), 0);
                  }}
                >
                  <FaSearch
                    className="profiles-member-picker__icon"
                    aria-hidden="true"
                  />
                  <input
                    ref={memberInputRef}
                    type="text"
                    value={memberQuery}
                    disabled={guildMembersLoading}
                    placeholder={
                      guildMembersLoading
                        ? 'Carregando membros…'
                        : 'Buscar membro ou colar Discord ID…'
                    }
                    onChange={(e) => {
                      setMemberQuery(e.target.value);
                      setMemberMenuOpen(true);
                      const raw = e.target.value.trim();
                      if (/^\d{5,32}$/.test(raw)) {
                        setLinkForm((prev) => ({ ...prev, discordId: raw }));
                      }
                    }}
                    onFocus={() => {
                      setMemberMenuOpen(true);
                      if (!guildMembers.length) onReloadMembers?.();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canUseRawId) {
                        e.preventDefault();
                        pickMember(rawIdDraft);
                      }
                    }}
                    aria-required="true"
                  />
                  <FaChevronDown
                    className="profiles-member-picker__chevron"
                    aria-hidden="true"
                  />
                </div>

                {memberMenuOpen && !guildMembersLoading && (
                  <div className="profiles-member-picker__menu" role="listbox">
                    {guildMembersError && guildMembers.length === 0 ? (
                      <p className="profiles-member-picker__empty">
                        {guildMembersError}
                        {canUseRawId
                          ? ' Você ainda pode colar o ID e pressionar Enter.'
                          : ''}
                        {onReloadMembers ? (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="profiles-member-picker__retry"
                              onClick={onReloadMembers}
                            >
                              Tentar de novo
                            </button>
                          </>
                        ) : null}
                      </p>
                    ) : filteredMembers.length === 0 ? (
                      <p className="profiles-member-picker__empty">
                        {guildMembers.length === 0
                          ? 'Nenhum membro carregado.'
                          : memberQuery.trim()
                            ? canUseRawId
                              ? 'Nenhum resultado — pressione Enter para usar este ID.'
                              : 'Nenhum membro encontrado.'
                            : 'Digite para filtrar os membros.'}
                      </p>
                    ) : (
                      filteredMembers.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          className="profiles-member-picker__option"
                          role="option"
                          onClick={() => pickMember(m)}
                        >
                          <DiscordUserChip user={m} userId={m.id} compact />
                          {m.username ? (
                            <span className="profiles-member-picker__username">
                              @{m.username}
                            </span>
                          ) : null}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {linkForm.discordId && !selectedMember?.displayName ? (
              <p className="profiles-link__raw-id">
                Discord ID: <code>{linkForm.discordId}</code>
              </p>
            ) : null}
          </div>

          <div className="profiles-link__grid">
            <label>
              Nick Minecraft
              <input
                value={linkForm.nickname}
                onChange={(e) =>
                  setLinkForm((prev) => ({
                    ...prev,
                    nickname: e.target.value,
                  }))
                }
                placeholder="Ex: Steve"
                maxLength={16}
                required
                autoComplete="off"
              />
            </label>
            <label>
              UUID (opcional)
              <input
                value={linkForm.uuid}
                onChange={(e) =>
                  setLinkForm((prev) => ({ ...prev, uuid: e.target.value }))
                }
                placeholder="Deixe vazio para UUID offline"
                autoComplete="off"
              />
            </label>
          </div>

          <div className="profiles-link__actions">
            <button
              type="submit"
              className="admin-btn admin-btn--primary"
              disabled={
                actionBusy === 'link' ||
                !linkForm.discordId ||
                !linkForm.nickname.trim()
              }
            >
              <FaLink aria-hidden="true" />
              {actionBusy === 'link' ? 'Vinculando…' : 'Criar vínculo'}
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--ghost"
              onClick={onToggleLink}
              disabled={actionBusy === 'link'}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="admin-stats admin-stats--wide profiles-stats">
        <div className="admin-stat">
          <span className="admin-stat__label">Total</span>
          <strong className="admin-stat__value">{stats?.total ?? '—'}</strong>
          <span className="admin-stat__sub">vínculos ativos</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Online</span>
          <strong className="admin-stat__value">{stats?.online ?? '—'}</strong>
          <span className="admin-stat__sub">lastSeen ≤ 2 min</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Offline</span>
          <strong className="admin-stat__value">{stats?.offline ?? '—'}</strong>
          <span className="admin-stat__sub">fora do jogo</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Com clã</span>
          <strong className="admin-stat__value">{stats?.withClan ?? '—'}</strong>
          <span className="admin-stat__sub">
            Códigos pendentes: {stats?.pendingCodes ?? '—'}
          </span>
        </div>
      </div>

      <div className="admin-toolbar profiles-toolbar">
        <div className="admin-filters">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'online', label: 'Online' },
            { id: 'offline', label: 'Offline' },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              className={`admin-chip${presenceFilter === s.id ? ' admin-chip--active' : ''}`}
              onClick={() => onPresenceFilter(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <form
          className="admin-search"
          onSubmit={(e) => {
            e.preventDefault();
            onSearch();
          }}
        >
          <FaSearch aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Nick, Discord ID, UUID, clã…"
          />
        </form>
      </div>

      <div className={`profiles-layout${selected ? ' has-detail' : ''}`}>
        <div className="admin-table-wrap profiles-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Jogador</th>
                <th>Discord</th>
                <th>Dinheiro</th>
                <th>Clã</th>
                <th>Status</th>
                <th>Vinculado</th>
              </tr>
            </thead>
            <tbody>
              {loading && profiles.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <p className="admin-empty">Carregando contas…</p>
                  </td>
                </tr>
              ) : profiles.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <p className="admin-empty">Nenhuma conta vinculada encontrada.</p>
                  </td>
                </tr>
              ) : (
                profiles.map((p) => {
                  const isSel = selected?.id === p.id;
                  const head = mcHead(p.nickname);
                  return (
                    <tr
                      key={p.id}
                      className={`admin-table__row profiles-row${isSel ? ' is-selected' : ''}`}
                      onClick={() => onSelect(p)}
                    >
                      <td>
                        <div className="profiles-player">
                          {head ? (
                            <img
                              src={head}
                              alt=""
                              className="profiles-player__head"
                              width={32}
                              height={32}
                            />
                          ) : (
                            <span className="profiles-player__head profiles-player__head--empty" />
                          )}
                          <strong>{p.nickname || '—'}</strong>
                        </div>
                      </td>
                      <td>
                        <DiscordUserChip
                          user={p.discordUser}
                          userId={p.discordId}
                          compact
                        />
                      </td>
                      <td>{formatMoney(p.money)}</td>
                      <td>{p.clan || 'Sem clã'}</td>
                      <td>
                        <span
                          className={`profiles-badge profiles-badge--${p.online ? 'ok' : 'muted'}`}
                        >
                          {p.online ? (
                            <>
                              <FaCheckCircle aria-hidden="true" /> Online
                            </>
                          ) : (
                            <>
                              <FaCircle aria-hidden="true" /> Offline
                            </>
                          )}
                        </span>
                      </td>
                      <td>{formatDate(p.linkedAt)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {selected && (
          <aside className="profiles-detail">
            <header className="profiles-detail__head">
              <div className="profiles-detail__title">
                {mcHead(selected.nickname) && (
                  <img
                    src={mcHead(selected.nickname)}
                    alt=""
                    width={48}
                    height={48}
                    className="profiles-detail__skin"
                  />
                )}
                <div>
                  <h2>{selected.nickname || 'Conta'}</h2>
                  <DiscordUserChip
                    user={selected.discordUser}
                    userId={selected.discordId}
                    compact
                  />
                </div>
              </div>
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={onCloseDetail}
                aria-label="Fechar"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </header>

            <div className="profiles-detail__badge-row">
              <span
                className={`profiles-badge profiles-badge--${selected.online ? 'ok' : 'muted'}`}
              >
                {selected.online ? 'Online no Nexus Origins' : 'Offline'}
              </span>
              {selected.verifiedInGame && (
                <span className="profiles-badge profiles-badge--info">
                  Verificado in-game
                </span>
              )}
              {selected.linkSource === 'admin_panel' && (
                <span className="profiles-badge profiles-badge--info">
                  Vínculo manual
                </span>
              )}
            </div>

            <dl className="profiles-detail__meta">
              <div>
                <dt>UUID</dt>
                <dd className="admin-mono">{selected.uuid || '—'}</dd>
              </div>
              <div>
                <dt>Discord ID</dt>
                <dd className="admin-mono">{selected.discordId || '—'}</dd>
              </div>
              <div>
                <dt>Última vez</dt>
                <dd>{formatDate(selected.lastSeen)}</dd>
              </div>
              <div>
                <dt>Atualizado</dt>
                <dd>{formatDate(selected.updatedAt)}</dd>
              </div>
              <div>
                <dt>Vinculado em</dt>
                <dd>{formatDate(selected.linkedAt)}</dd>
              </div>
            </dl>

            <div className="profiles-detail__edit">
              <h3>Editar / forçar sync</h3>
              <label>
                Nick Minecraft
                <input
                  value={editNick}
                  onChange={(e) => setEditNick(e.target.value)}
                  maxLength={16}
                />
              </label>
              <label>
                Dinheiro
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editMoney}
                  onChange={(e) => setEditMoney(e.target.value)}
                />
              </label>
              <label>
                Clã
                <input
                  value={editClan}
                  onChange={(e) => setEditClan(e.target.value)}
                  placeholder="Sem clã"
                  maxLength={48}
                />
              </label>
              <label className="profiles-detail__check">
                <input
                  type="checkbox"
                  checked={editOnline}
                  onChange={(e) => setEditOnline(e.target.checked)}
                />
                Marcar como online (forçar)
              </label>
              <button
                type="button"
                className="admin-btn admin-btn--primary admin-btn--sm"
                disabled={actionBusy === 'update' || !canEdit}
                onClick={saveEdits}
              >
                {actionBusy === 'update' ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </div>

            {canDelete && (
            <div className="profiles-detail__actions">
              <button
                type="button"
                className="admin-btn admin-btn--danger admin-btn--sm"
                disabled={actionBusy === 'unlink'}
                onClick={() => setConfirmUnlink(selected)}
              >
                <FaUnlink aria-hidden="true" /> Desvincular
              </button>
            </div>
            )}
            {!canEdit && !canDelete && (
              <p className="admin-hint">Acesso somente leitura nesta área.</p>
            )}
          </aside>
        )}
      </div>

      {pendingCodes.length > 0 && (
        <div className="profiles-codes">
          <h2>Códigos pendentes</h2>
          <p>Códigos gerados no jogo ainda não usados (ou não expirados).</p>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nick</th>
                  <th>Origem</th>
                  <th>Expira</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pendingCodes.map((c) => (
                  <tr key={c.id}>
                    <td className="admin-mono">{c.code}</td>
                    <td>{c.nickname || '—'}</td>
                    <td>{c.origin || '—'}</td>
                    <td>{formatDate(c.expiresAt)}</td>
                    <td>
                      {canEdit ? (
                      <button
                        type="button"
                        className="admin-btn admin-btn--ghost admin-btn--sm"
                        disabled={actionBusy === 'revoke_code'}
                        onClick={() => setConfirmRevoke(c)}
                      >
                        <FaBan aria-hidden="true" /> Invalidar
                      </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirmUnlink)}
        tone="danger"
        title="Desvincular conta?"
        message={
          confirmUnlink
            ? `Remover o vínculo de ${confirmUnlink.nickname} (${confirmUnlink.discordId})? O jogador precisará vincular de novo.`
            : ''
        }
        confirmLabel="Desvincular"
        onCancel={() => setConfirmUnlink(null)}
        onConfirm={() => {
          const id = confirmUnlink?.id;
          setConfirmUnlink(null);
          if (id) onUnlink(id);
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmRevoke)}
        tone="danger"
        title="Invalidar código?"
        message={
          confirmRevoke
            ? `Invalidar o código ${confirmRevoke.code} de ${confirmRevoke.nickname || '?'}?`
            : ''
        }
        confirmLabel="Invalidar"
        onCancel={() => setConfirmRevoke(null)}
        onConfirm={() => {
          const code = confirmRevoke;
          setConfirmRevoke(null);
          if (code) onRevokeCode(code);
        }}
      />
    </section>
  );
};

export default LinkedProfilesPanel;
